import './scss/style.scss'
// import './tailwind.css'

import { mount, hydrate } from 'svelte'
import { readable, writable } from 'svelte/store'

import { MobileModal } from './lib'
import { HeaderButtons, Search, MainSearch } from './lib/components'
import { addDepartmentSidebarMenuFunctionality } from './lib/util'
import { addDepartmentMainMenuFunctionality } from './lib/util'

import type { Readable, Writable } from 'svelte/store'

/**
* If grabbing a reference to an HTML element on a page, always cache it in a variable to avoid 
* redundant document queries!
* 
* This should be done for any global or frequently-used UI, but *only once*. If you're working on 
* something we only use once (or otherwise very sparsely), it probably shouldn't be in the main bundle — there's nothing
* wrong with creating standalone JS/CSS for your .Net widget, microsites, landing pages and the sort that aren't needed
* globally.
*/

/**
* Global UI elements.
* 
* If you need to do something on all or most pages, add it to our little UI cache, add it here. Then you can reference 
*/
const UI: { [property: string]: HTMLElement | null } = {
    header: document.getElementById('com-header'),
    headerButtons: document.querySelector('#com-header .right'),
    menu: document.querySelector('#com-header .lower'),
    news: document.querySelector('#com-home-news'),
    eprofSearchWidget: document.querySelector('.research-directory-search'),
    mainMenu: document.getElementById('com-main-menu'),
    departmentVerticalMenu: document.getElementById('department-navigation--vertical'),
    departmentMenu: document.getElementById('department-navigation'),
}

const mobileMenuVizStore: Writable<boolean> = writable(false)
const desktopSearchVizStore: Writable<boolean> = writable(false)
const divisionsMainMenuVizStore: Writable<boolean> = writable(false)

const chunks: string[] = window.location.pathname.substring(1).split('/')
const pathChunks: Readable<string[]> = readable(chunks)
const isDivision: boolean = chunks[2] === 'divisions'

const mobileMenuMount = document.createElement('div')
mobileMenuMount.id = 'mobile-menu-mount'
document.body.append(mobileMenuMount)

const portalDiv = document.createElement('div')
portalDiv.id = 'portal'
document.body.append(portalDiv)

// if(UI.menu) {
//   new Menu({
//     target: UI.menu,
//     props: {
//       divisionsMainMenuVizStore,
//       isDivision,
//       menu,
//       mobileMenuVizStore,
//       pathChunks,
//       desktopSearchVizStore,
//     },
//     hydrate: true,
//   })
// }

const menuItems = document.querySelectorAll('.desktop-main-menu .has-kids > .top-lvl')
const menuItems2ndlvl = document.querySelectorAll('.desktop-main-menu .has-kids > a + button')

menuItems.forEach(menuItem => {
    menuItem.addEventListener('click', (event) => {
        if (menuItem.getAttribute('href') == 'javascript.void(0)') {
            event.preventDefault() // Prevent default anchor behavior
        }
        const submenuWrapper = menuItem.nextElementSibling as HTMLElement // Get the submenu wrapper
        const parentLi = menuItem.parentElement as HTMLElement // Get the parent <li> element


        // Check if the clicked menu is already open
        if (parentLi.classList.contains('show')) {
            // Close the current menu
            submenuWrapper?.classList.remove('show')
            parentLi.classList.remove('show')

            let toggle = parentLi.querySelector('a')
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false')
            }
            return // Exit here to avoid reopening the same menu
        }

        // Close other submenus at the same level (either top-level or third-level)
        if (isTopLevel(parentLi)) {
            closeAllTopLevelSubmenus(submenuWrapper, parentLi) // Close other top-level submenus
        } else {
            closeAllThirdLevelSubmenus(submenuWrapper, parentLi) // Close other third-level submenus
        }

        // Open the current menu
        submenuWrapper?.classList.add('show')
        parentLi.classList.add('show')

        let toggle = parentLi.querySelector('a')
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'true')
        }

    })
})
menuItems2ndlvl.forEach(menuItem => {
    menuItem.addEventListener('click', (event) => {
        if (menuItem.getAttribute('href') == 'javascript.void(0)') {
            event.preventDefault() // Prevent default anchor behavior
        }
        const submenuWrapper = menuItem.nextElementSibling as HTMLElement // Get the submenu wrapper
        const parentLi = menuItem.parentElement as HTMLElement // Get the parent <li> element

        // Check if the clicked menu is already open
        if (parentLi.classList.contains('show')) {
            // Close the current menu
            submenuWrapper?.classList.remove('show')
            parentLi.classList.remove('show')

            let toggle = parentLi.querySelector('a')
            if (toggle) {
                toggle.setAttribute('aria-expanded', 'false')
            }
            return // Exit here to avoid reopening the same menu
        }

        // Close other submenus at the same level (either top-level or third-level)
        if (isTopLevel(parentLi)) {
            closeAllTopLevelSubmenus(submenuWrapper, parentLi) // Close other top-level submenus
        } else {
            closeAllThirdLevelSubmenus(submenuWrapper, parentLi) // Close other third-level submenus
        }

        // Open the current menu
        submenuWrapper?.classList.add('show')
        parentLi.classList.add('show')

        let toggle = parentLi.querySelector('a')
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'true')
        }
    })
})

if (UI.mainMenu) {
    const desktopMenu = document.querySelector('ul.desktop-main-menu')
    const departmentMenu = document.getElementById('department-navigation')?.querySelector('ul')

    mount(MobileModal, {
        target: mobileMenuMount,
        props: {
            desktopMenu,
            departmentMenu,
            mobileMenuVizStore,
            pathChunks,
        }
    })

    hydrate(HeaderButtons, {
        target: UI.headerButtons,
        props: {
            desktopSearchVizStore,
            divisionsMainMenuVizStore,
            mobileMenuVizStore,
            isDivision,
        }
    })
}

if (UI.header) {
    mount(MainSearch, {
        target: UI.header,
        props: { desktopSearchVizStore }
    })
}

// Function to check if the element is a top-level menu item
function isTopLevel(parentLi: HTMLElement): boolean {
    return parentLi.parentElement?.classList.contains('desktop-main-menu')
}

// Close other top-level submenus
function closeAllTopLevelSubmenus(currentSubmenu: HTMLElement, currentLi: HTMLElement) {
    document.querySelectorAll('.desktop-main-menu .show').forEach(item => {
        item.classList.remove('show')
    })
}

// Close other third-level submenus
function closeAllThirdLevelSubmenus(currentSubmenu: HTMLElement, currentLi: HTMLElement) {
    const thirdLevelItems = document.querySelectorAll('.has-kids.dropdown-submenu > .submenu-wrapper')

    thirdLevelItems.forEach(submenu => {
        if (submenu !== currentSubmenu) {
            submenu.classList.remove('show')
            const parentLi = submenu.parentElement as HTMLElement
            if (parentLi) {
                parentLi.classList.remove('show')
            }
        }
    })
}

// Close submenus when clicking outside of the menu
const specifiedElement = document.getElementById('com-main-menu')

document.addEventListener('click', event => {
    const isClickInside = specifiedElement?.contains(event.target as Node)

    if (!isClickInside) {
        const hideshow = document.querySelectorAll('.has-kids')
        hideshow.forEach(i => {
            i.classList.remove('show')
        })
    }
})

document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
        // Close all menus on Esc key press
        const hideshow = document.querySelectorAll('.has-kids')
        hideshow.forEach(i => {
            i.classList.remove('show')
        })
    }
})

//if(UI.headerButtons) {
//  new HeaderButtons({ 
//    target: UI.headerButtons,
//    props: {
//      desktopSearchVizStore,
//      divisionsMainMenuVizStore,
//      mobileMenuVizStore,
//      isDivision,
//    },
//    hydrate: true,
//  })
//}

if (UI.eprofSearchWidget) {
    hydrate(Search, {
        target: UI.eprofSearchWidget,
        props: {
            directoryMode: true,
        },
    })
}

addDepartmentSidebarMenuFunctionality(UI.departmentVerticalMenu)
addDepartmentMainMenuFunctionality(UI.departmentMenu)

var newDate = new Date(),
    thisYear = newDate.getFullYear();
document.querySelectorAll('.current-year').forEach(yearset => {
    yearset.innerHTML = thisYear +" "
});